// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { MemberRole, ServerEvent } from '@/models';
import type { Channel, ChannelType } from '@/models/channel.ts';
import type { Server } from '@/models/server.ts';
import { create } from 'zustand';

export type ModalType =
  | 'createServer'
  | 'invite'
  | 'editServer'
  | 'members'
  | 'createChannel'
  | 'leaveServer'
  | 'deleteServer'
  | 'deleteChannel'
  | 'editChannel'
  | 'messageFile'
  | 'deleteMessage'
  | 'deleteUser'
  | 'incomingCall'
  | 'createServerEvent'
  | 'editServerEvent';

export interface ModalData {
  server?: Server;
  channel?: Channel;
  channelType?: ChannelType;
  apiUrl?: string;
  query?: Record<string, any>;
  status?: string;
  userId?: string;
  role?: MemberRole;
  conversationId?: string;
  userName?: string;
  avatarUrl?: string;
  setServer?: (server: Server) => void;
  audioChannels?: Channel[];
  videoChannels?: Channel[];
  event?: ServerEvent;
}

interface ModalStore {
  type: ModalType | null;
  data: ModalData;
  isOpen: boolean;
  onOpen: (type: ModalType, data?: ModalData) => void;
  onClose: () => void;
  setIsOpen: (isOpen: boolean) => void;
}

export const useModal = create<ModalStore>((set, get) => ({
  type: null,
  data: {},
  isOpen: false,
  onOpen: (type, data = {}) => {
    const { isOpen, type: currentType } = get();
    if (isOpen && currentType === type) return; // ignore
    set({ isOpen: true, type, data });
  },
  onClose: () => set({ type: null, isOpen: false, data: {} }),
  setIsOpen: (isOpen) => set({ isOpen }),
}));

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ModalData;
}
